import { Workbox } from 'workbox-window';
import { buildInfo } from './build-info';
import { esCacheDelServiceWorker } from './reglasServiceWorker';

/** Cada cuánto una pestaña abierta pregunta al servidor si hay un despliegue nuevo. */
const INTERVALO_BUSQUEDA_ACTUALIZACION_MS = 60 * 60 * 1000;

/**
 * Cuánto se espera a que la versión pedida tome el control. Si no llega (p. ej. la que esperaba fue
 * reemplazada por otra más nueva), se vuelve a pedir en la siguiente navegación: nunca se recarga
 * sin que la versión nueva controle la pestaña, y si el control llega tarde se recarga al navegar.
 */
const ESPERA_TOMA_DE_CONTROL_MS = 3000;

/**
 * Si después de pedir la recarga la pestaña sigue viva, la recarga se canceló (p. ej. el aviso de
 * cambios sin guardar del buzón): se vuelve a intentar en la siguiente navegación.
 */
const ESPERA_RECARGA_CANCELADA_MS = 1000;

/**
 * Build (commit y fecha) que ya recargó por un archivo que no cargó: una sola recarga automática por
 * build, para no entrar en un ciclo si el archivo falla siempre (no por una versión vieja).
 */
const CLAVE_BUILD_RECARGADO = 'anfora:build-recargado-por-archivo';

/** Lo que la app usa de workbox-window; los tests lo reemplazan por un doble. */
export interface ServiceWorkerDeLaApp {
  addEventListener(tipo: 'waiting' | 'controlling', escucha: () => void): void;
  register(opciones: {
    immediate: boolean;
  }): Promise<ServiceWorkerRegistration | undefined>;
  messageSkipWaiting(): void;
}

interface DependenciasRegistro {
  /** Vite dev server (`nx serve`). qa, demo, test y producción son builds: ahí sí hay SW. */
  esServidorDeDesarrollo?: boolean;
  /** Si un SW controlaba la pestaña al cargarla. Por defecto, navigator.serviceWorker.controller. */
  teniaControlador?: boolean;
  crearServiceWorker?: () => ServiceWorkerDeLaApp;
  recargarPagina?: () => void;
  /** Commit y fecha del build (defines __BUILD_COMMIT__ y __BUILD_AT__, vía build-info.ts). */
  identidadDelBuild?: string;
}

function recargarLaPestana(): void {
  window.location.reload();
}

// Estado de esta pestaña.
let serviceWorker: ServiceWorkerDeLaApp | undefined;
let registroActual: ServiceWorkerRegistration | undefined;
let recargarPagina = recargarLaPestana;
let identidadBuild = '';
/** Un SW controlaba la pestaña al cargarla: el bundle que corre es el de la versión anterior. */
let teniaControladorAlCargar = false;
/** Esta pestaña vio una versión nueva instalada esperando. */
let versionPendiente = false;
/**
 * Esta pestaña pidió activarla (SKIP_WAITING) y espera a que tome el control. Caduca sola a los
 * ESPERA_TOMA_DE_CONTROL_MS si el control no llegó, para que otra navegación pueda volver a pedirla.
 */
let solicitada = false;
/**
 * La versión nueva ya controla la pestaña: recargar en el siguiente cambio de pantalla. También es
 * el camino de reintento si se canceló la recarga que pidió esta misma pestaña.
 */
let recargarAlNavegar = false;
/**
 * Hay una recarga en curso: evita pedir otra. Se libera si la pestaña sigue viva después de
 * ESPERA_RECARGA_CANCELADA_MS (recarga cancelada).
 */
let recargando = false;

/**
 * Registra el service worker de la app (/sw.js, generado desde sw.ts) con workbox-window.
 *
 * Fuera del servidor de desarrollo (producción, qa, demo, test) la versión nueva que se publique se
 * instala y queda esperando; nunca se recarga a mitad de lo que el usuario hace. Se aplica al
 * cambiar de pantalla (aplicarActualizacionPendiente). Cada hora se pregunta por una versión nueva,
 * para las pestañas que pasan el día abiertas. Y si una pantalla no carga porque sus archivos ya no
 * existen tras una publicación, se recarga una vez sobre la versión nueva.
 *
 * En el servidor de desarrollo no se registra nada y se da de baja el SW de desarrollo que hubiera
 * quedado de sesiones anteriores (`dev-sw.js`), junto con sus cachés: servía módulos viejos de Vite
 * y llegó a dejar la app en blanco.
 */
export async function registrarServiceWorker({
  esServidorDeDesarrollo = typeof __SERVIDOR_DE_DESARROLLO__ === 'boolean' &&
    __SERVIDOR_DE_DESARROLLO__,
  teniaControlador,
  crearServiceWorker = () => new Workbox('/sw.js', { scope: '/' }),
  recargarPagina: recargar = recargarLaPestana,
  identidadDelBuild = `${buildInfo.commit}@${buildInfo.builtAt}`,
}: DependenciasRegistro = {}): Promise<void> {
  if (esServidorDeDesarrollo) {
    if ('serviceWorker' in navigator) await limpiarServiceWorkerDeDesarrollo();
    return;
  }

  recargarPagina = recargar;
  identidadBuild = identidadDelBuild;
  window.addEventListener('vite:preloadError', alFallarCargaDeArchivo);

  if (!('serviceWorker' in navigator)) return;

  teniaControladorAlCargar =
    teniaControlador ?? Boolean(navigator.serviceWorker.controller);
  const sw = crearServiceWorker();
  serviceWorker = sw;
  // Incluye la versión que otra pestaña encontró (isExternal) y la que ya esperaba al abrir esta.
  sw.addEventListener('waiting', () => {
    versionPendiente = true;
  });
  sw.addEventListener('controlling', alTomarControlVersionNueva);

  try {
    const registro = await sw.register({ immediate: true });
    if (!registro) return;
    registroActual = registro;
    setInterval(() => {
      void buscarActualizacion(registro);
    }, INTERVALO_BUSQUEDA_ACTUALIZACION_MS);
  } catch (error) {
    console.error('No se pudo registrar el service worker', error);
  }
}

/**
 * Si hay una versión nueva, la aplica: se llama al cambiar de pantalla. Sin red no hace nada (la
 * recarga de una ruta que no está en caché terminaría en la página de error del navegador).
 */
export function aplicarActualizacionPendiente(): void {
  if (!navigator.onLine || recargando) return;

  if (recargarAlNavegar) {
    recargarUnaVez();
    return;
  }

  // Además del evento 'waiting', se mira la versión que espera ahora mismo: workbox-window no
  // siempre avisa (p. ej. si ya se estaba instalando al registrar).
  const hayVersionEsperando = versionPendiente || Boolean(registroActual?.waiting);
  if (!hayVersionEsperando || solicitada || !serviceWorker) return;

  solicitada = true;
  serviceWorker.messageSkipWaiting();
  // Si ya se está recargando, el control llegó a tiempo: no hace falta habilitar otro pedido.
  setTimeout(() => {
    if (!recargando) solicitada = false;
  }, ESPERA_TOMA_DE_CONTROL_MS);
}

/**
 * Una versión nueva tomó el control de esta pestaña. Si la pidió esta pestaña, recarga ya (acaba de
 * cambiar de pantalla). Si la activó otra y esta corría una versión anterior (la controlaba un SW
 * al cargar, o vio la nueva esperando), recarga en su propio siguiente cambio de pantalla. Si no, es
 * la primera instalación del SW: la página ya trae el bundle actual.
 */
function alTomarControlVersionNueva(): void {
  if (solicitada) {
    recargarAlNavegar = true;
    recargarUnaVez();
  } else if (teniaControladorAlCargar || versionPendiente) {
    recargarAlNavegar = true;
  }
}

function recargarUnaVez(): void {
  if (recargando) return;
  recargando = true;
  recargarPagina();
  setTimeout(() => {
    recargando = false;
  }, ESPERA_RECARGA_CANCELADA_MS);
}

/**
 * Vite avisa con `vite:preloadError` cuando no puede cargar los archivos de una pantalla: tras una
 * publicación, el bundle viejo pide archivos que ya no existen y la pantalla quedaba en blanco. Se
 * recarga una vez sobre la versión nueva. Sin red no (el fallo es de la red), y si este mismo build
 * ya recargó por esto tampoco: el error sigue su curso en vez de entrar en un ciclo de recargas.
 */
function alFallarCargaDeArchivo(evento: VitePreloadErrorEvent): void {
  if (!navigator.onLine) return;

  try {
    if (esteBuildYaRecargoPorArchivo()) return;
    anotarRecargaPorArchivo();
  } catch {
    // Sin sessionStorage no hay forma de recargar una sola vez: el error sigue su curso.
    return;
  }

  console.warn(
    'No se pudo cargar un archivo de la app; se recarga con la versión publicada',
    evento.payload
  );
  evento.preventDefault();
  recargarPagina();
}

function esteBuildYaRecargoPorArchivo(): boolean {
  return sessionStorage.getItem(CLAVE_BUILD_RECARGADO) === identidadBuild;
}

function anotarRecargaPorArchivo(): void {
  sessionStorage.setItem(CLAVE_BUILD_RECARGADO, identidadBuild);
}

async function buscarActualizacion(
  registro: ServiceWorkerRegistration
): Promise<void> {
  // Sin red la búsqueda falla; y si ya se está instalando una versión no hace falta pedir otra.
  if (!navigator.onLine || registro.installing) return;

  try {
    await registro.update();
  } catch {
    // Servidor caído o red intermitente: se reintenta en el siguiente intervalo.
  }
}

async function limpiarServiceWorkerDeDesarrollo(): Promise<void> {
  try {
    const registros = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registros.filter(esServiceWorkerDeDesarrollo).map((r) => r.unregister())
    );

    if (typeof caches === 'undefined') return;
    const nombres = await caches.keys();
    await Promise.all(
      nombres.filter(esCacheDelServiceWorker).map((n) => caches.delete(n))
    );
  } catch (error) {
    console.warn('No se pudo limpiar el service worker de desarrollo', error);
  }
}

function esServiceWorkerDeDesarrollo(registro: ServiceWorkerRegistration): boolean {
  const script =
    registro.active?.scriptURL ??
    registro.waiting?.scriptURL ??
    registro.installing?.scriptURL ??
    '';
  return script.includes('dev-sw.js');
}
